import React, { useState, useEffect } from 'react';
import { Paper, Typography, Stack, TextField, MenuItem } from '@mui/material';

const PacingPortfolioInformation = ({ portfolioInfo, handlePortfolioInfoChange }) => {
  // State to track the input value before formatting
  const [marketValueInput, setMarketValueInput] = useState('');
  const [growthRateInput, setGrowthRateInput] = useState('');

  // Update local state when props change
  useEffect(() => {
    setMarketValueInput(formatCurrency(portfolioInfo.portfolioMarketValue));
    setGrowthRateInput(formatPercentage(portfolioInfo.growthRate));
  }, [portfolioInfo]);

  // Format numbers for display
  const formatCurrency = (value) => {
    if (!value) return '';
    return Number(value).toLocaleString();
  };

  // Format percentage for display
  const formatPercentage = (value) => {
    if (!value) return '';
    return (Number(value) * 100).toFixed(2);
  };

  // Generate quarter-end dates for the past 5 years
  const generateQuarterOptions = () => {
    const options = [];
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    
    for (let year = currentYear; year >= currentYear - 5; year--) {
      for (let quarter = 4; quarter >= 1; quarter--) {
        const monthEnd = new Date(year, quarter * 3, 0);
        const dateString = monthEnd.toISOString().split('T')[0];
        const label = `${year} Q${quarter}`;
        options.push({ label, value: dateString });
      }
    }
    return options;
  };

  const handleMarketValueChange = (e) => {
    const rawValue = e.target.value.replace(/[^\d.]/g, '');
    setMarketValueInput(rawValue);
    
    if (!isNaN(rawValue)) {
      handlePortfolioInfoChange('portfolioMarketValue', Number(rawValue));
    }
  };

  const handleMarketValueBlur = () => {
    setMarketValueInput(formatCurrency(portfolioInfo.portfolioMarketValue));
  };

  const handleGrowthRateChange = (e) => {
    const rawValue = e.target.value;
    setGrowthRateInput(rawValue);
    
    if (!isNaN(rawValue)) {
      handlePortfolioInfoChange('growthRate', Number(rawValue) / 100);
    }
  };

  const handleGrowthRateBlur = () => {
    setGrowthRateInput(formatPercentage(portfolioInfo.growthRate));
  };

  return (
    <Paper sx={{ 
      p: 2, 
      mb: 3,
      transition: 'transform 0.2s ease-in-out',
      '&:hover': {
        transform: 'translateY(-5px)',
        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.2)',
      },
    }}>
      <Typography variant="subtitle1" gutterBottom>Portfolio Information</Typography>
      <Stack 
        direction={{ xs: 'column', md: 'row' }} 
        spacing={2} 
        mt={1}
        sx={{
          '& .MuiTextField-root': {
            width: { xs: '100%', md: 'auto' },
            minWidth: { md: 140 }
          }
        }}
      >
        <TextField
          select
          label="Portfolio Base Currency"
          value={portfolioInfo.portfolioBaseCurrency || 'CAD'}
          onChange={(e) => handlePortfolioInfoChange('portfolioBaseCurrency', e.target.value)}
        >
          <MenuItem value="USD">USD</MenuItem>
          <MenuItem value="EUR">EUR</MenuItem>
          <MenuItem value="CAD">CAD</MenuItem>
          <MenuItem value="CNY">CNY</MenuItem>
          <MenuItem value="AUD">AUD</MenuItem>
          <MenuItem value="JPY">JPY</MenuItem>
          <MenuItem value="GBP">GBP</MenuItem>
          <MenuItem value="CHF">CHF</MenuItem>
          <MenuItem value="HKD">HKD</MenuItem>
          <MenuItem value="NZD">NZD</MenuItem>
        </TextField>
        
        <TextField
          label="Portfolio Market Value"
          value={marketValueInput}
          onChange={handleMarketValueChange}
          onBlur={handleMarketValueBlur}
        />
        
        <TextField
          select
          label="As-of Date"
          value={portfolioInfo.asOfDate ? portfolioInfo.asOfDate.split('T')[0] : ''}
          onChange={(e) => handlePortfolioInfoChange('asOfDate', e.target.value)}
        >
          {generateQuarterOptions().map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        
        <TextField
          label="Growth Rate (%)"
          value={growthRateInput}
          onChange={handleGrowthRateChange}
          onBlur={handleGrowthRateBlur}
        />
      </Stack>
    </Paper>
  );
};

export default React.memo(PacingPortfolioInformation);